import { makeAxios } from '../redux/thunks/validation';
import { ThunkActionType } from '../redux/actions/types';

// API configuration
// Removing direct API URL and key references from frontend code
// const AI_PROBE_API_URL = process.env.AI_PROBE_API_URL;
// const AI_PROBE_API_KEY = process.env.AI_PROBE_API_KEY;

// Request throttling
const recentRequests = new Map<string, number>();
const REQUEST_THROTTLE_MS = 0; // Temporarily disabled throttling to test API response
// const REQUEST_THROTTLE_MS = 2000; // 2 seconds

// Global flag to track if API has failed
export let API_HAS_FAILED = false;

// Generate a simple hash for the answer and context to use as key
const generateRequestKey = (answer: string, context: any): string => {
    return `${answer.substring(0, 50)}_${context?.questionId || ''}`;
};

/**
 * Represents the result of probing API call
 */
export interface ProbeResult {
    questionText: string;
    shouldContinue: boolean;
}

/**
 * Generate probed questions by calling the Azure Function API
 * 
 * @param answer The user's answer that triggered the probing
 * @param mainVariableId The ID of the main variable
 * @param variableName The name of the main variable
 * @param questionId The ID of the question
 * @param questionText The original question text
 * @param context Additional context for the API
 * @returns Array of generated question objects 
 */
export const generateProbedQuestions = (
    answer: string,
    mainVariableId: string,
    variableName: string,
    questionId: string,
    questionText?: string, // The original question text
    context?: any, // Additional context for the API
    settings?: { [key: string]: any }
): ThunkActionType<Promise<any>> => {
    return async (dispatch, getState) => {
        try {
            // If API has failed previously, skip probing immediately
            if (API_HAS_FAILED) {
                return [];
            }
            
            // Get question text with fallbacks
            const Q = (questionText && questionText.trim() !== '') 
                ? questionText 
                : context?.questionText && context.questionText.trim() !== ''
                    ? context.questionText
                    : "Please provide your thoughts on this topic";
            
            // Create context with the question ID for proper request tracking
            const apiContext = { 
                questionText: Q,
                questionId,
                mainVariableId,
                variableName, // Add variableName to context
                ...context // Include any other context properties
            };
            
            // Generate a key for this request to check recent calls
            const requestKey = generateRequestKey(answer, apiContext);
            const now = new Date().getTime(); // UTC timestamp in milliseconds
            const lastRequestTime = recentRequests.get(requestKey);
            
            // Throttle duplicate requests
            if (lastRequestTime && (now - lastRequestTime) < REQUEST_THROTTLE_MS) {
                // Skip probing by returning empty array
                return [];
            }
            
            // Call the API to get a probed question
            const probeResult = await dispatch(getAIProbe(answer, apiContext));
            
            if (!probeResult) {
                // When [NotEngaged] is detected or we get null for any reason,
                // return a special marker with shouldContinue=false
                // This ensures no UI elements will be shown but we'll know probing should stop
                return [{
                    id: `${variableName}_AI_PROBING_1`,
                    questionText: "", // Empty question text so nothing will show
                    order: 1,
                    mainVariableId,
                    answer: "",
                    questionId,
                    shouldContinue: false, // The key flag needed to stop probing
                    settings
                }];
            }
            
            // Return an array with a single question
            return [{
                id: `${variableName}_AI_PROBING_1`,
                questionText: probeResult.questionText,
                order: 1,
                mainVariableId,
                answer: "",
                questionId,
                shouldContinue: probeResult.shouldContinue,
                settings
            }];
        } catch (error) {
            console.error("Error generating probed questions:", error);
            // Set the global flag to skip all future probing
            API_HAS_FAILED = true;
            return []; // Return empty array instead of fallback question
        }
    };
};

/**
 * Gets a follow-up question from the AI Probing service
 * 
 * @param answer The user's answer to analyze
 * @param context Additional context information
 * @returns A promise that resolves to a probed question
 */
export function getAIProbe(answer: string, context: any = {}): ThunkActionType<Promise<ProbeResult | null>> {
    return async (dispatch, getState) => {
        if (!answer || answer.trim().length === 0) {
            return null;
        }
        
        // If API has failed previously, skip probing immediately
        if (API_HAS_FAILED) {
            return null;
        }
        
        try {
            // Generate a key for this request
            const requestKey = generateRequestKey(answer, context);
            
            // Check if we've made this exact request recently
            const now = new Date().getTime(); // UTC timestamp in milliseconds
            const lastRequestTime = recentRequests.get(requestKey);
            
            if (lastRequestTime && (now - lastRequestTime) < REQUEST_THROTTLE_MS) {
                // Return null for throttled requests or could return a cached response
                return null;
            }
            
            // Update the timestamp for this request
            recentRequests.set(requestKey, now);
            
            // Clean up old requests (anything older than 1 minute)
            Array.from(recentRequests.entries()).forEach(([key, timestamp]) => {
                if (now - timestamp > 60000) {
                    recentRequests.delete(key);
                }
            });
            
            // Make sure we have a valid question text
            const questionText = context?.questionText && context.questionText.trim() !== '' 
                ? context.questionText 
                : "Please share your thoughts on this topic";
            
            // Prepare the request payload for the backend API
            const payload = {
                AnswerText: answer,
                MainVariableId: context.mainVariableId || "",
                VariableId: context.questionId || "", // Use questionId as VariableId if not explicitly provided
                VariableName: context.variableName || "", // Remove reference to undefined variableName
                QuestionId: context.questionId || "",
                MaxProbes: 1, // Since we're only getting one probe question
                Context: {
                    QuestionText: questionText,
                    DontLead: context.dontlead !== undefined ? context.dontlead : false,
                    Unengaged: context.unengaged !== undefined ? context.unengaged : false,
                    UserContext: context.usercontext || "",
                    // Add conversation history if available
                    PreviousQA: context.previousQA || []
                }
            };
            
            // Call the backend API endpoint using makeAxios
            // Try with absolute URL to see if that's causing an issue
            let apiUrl = '/Probing/Generate';
            
            try {
                const response = await dispatch(makeAxios()).post(apiUrl, payload);
                // Check if the response is valid
                if (response.data) {
                    // The backend returns a list of probed questions, take the first one
                    if (Array.isArray(response.data) && response.data.length > 0) {
                        const probeQuestion = response.data[0];
                        console.log("probeQuestion:", probeQuestion);
                        return {
                            questionText: probeQuestion.questionText,
                            shouldContinue: probeQuestion.shouldContinue
                        };
                    }
                    // Handle case where backend returns a completion object directly
                    else if (response.data.completion) {
                        const completion = response.data.completion;
                        
                        if (completion.choices && completion.choices.length > 0) {
                            const content = completion.choices[0].message.content;
                            const finishReason = completion.choices[0].finish_reason;
                            
                            if (content.includes('[NotEngaged]')) {
                                return null;
                            }
                            
                            return {
                                questionText: content,
                                shouldContinue: finishReason !== "stop"
                            };
                        }
                    }
                }
                
                // If we couldn't extract a question from the response, return null
                console.warn('Could not extract probed question from API response, skipping probe');
                return null;
                
            } catch (error) {
                console.error('Error calling AI probing service:', error);
                // Set the global flag to skip all future probing
                API_HAS_FAILED = true;
                return null; // Return null to skip probing and proceed with navigation
            }
        } catch (error) {
            console.error('Error calling AI probing service:', error);
            // Set the global flag to skip all future probing
            API_HAS_FAILED = true;
            return null; // Return null to skip probing and proceed with navigation
        }
    }
}